import React, { useEffect, useId, useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { NumberField } from '@/components/inspector-fields/NumberField/NumberField';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import clsx from 'clsx';

// Props for the slider field
interface SliderFieldProps {
  value?: number; // current value
  onChange: (value: number) => void; // callback when value changes
  onChangeComplete?: (value: number, initialValue: number) => void;
  label: string; // label text
  className?: string; // extra class for wrapper
  classNameInput?: string; // extra class for input
  min: number; // min value
  max: number; // max value
  step?: number; // step size
  showMaxLabel?: boolean; // show 'MAX' if at max
  suffix?: string; // text after number (like px)
  allowDecimal?: boolean; // allow decimals
  disabled?: boolean;
  tooltip?: string; // tooltip text shown via info icon next to label
}

export const SliderField: React.FC<SliderFieldProps> = ({
  value = 0,
  onChange = () => {},
  label = 'Stroke',
  className,
  classNameInput,
  min = 0,
  max = 100,
  step,
  showMaxLabel = false,
  suffix,
  allowDecimal = false,
  onChangeComplete,
  disabled,
  tooltip,
}) => {
  const [initialState, setInitialState] = useState(value);
  // Local display state to ensure immediate feedback while dragging the slider
  const [displayValue, setDisplayValue] = useState<number>(value);
  const [isDragging, setIsDragging] = useState(false);
  const actualStep = step ?? (allowDecimal ? 0.01 : 1); // step size
  // Generate unique ID for label/input association
  const inputId = useId();

  // Show value with suffix (like 10px)
  function formatDisplayValue(val: number | string) {
    if (showMaxLabel && Number(val) === max) {
      return 'MAX';
    }

    if (allowDecimal) {
      const num = Number(val);
      return !isNaN(num) ? num.toFixed(2) + (suffix || '') : String(val); // Changed from toFixed(1) to toFixed(2)
    }

    return String(val) + (suffix || '');
  }

  // Remove suffix from value (get just number)
  function getNumericInput(val: string) {
    if (suffix && val.endsWith(suffix)) {
      return val.slice(0, -suffix.length);
    }
    return val;
  }

  // Clamp value and call onChange
  const handleChange = (val: number) => {
    const clampedValue = Math.min(Math.max(val, min), max);
    setDisplayValue(clampedValue);
    onChange(clampedValue);
  };

  const onChangeCompleteValues = (value: number) => {
    const initialValue = initialState || 0;
    onChangeComplete?.(value, initialValue);
    setInitialState(value);
  };
  const handleChangeComplete = (
    event: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    onChangeCompleteValues(Number(event.currentTarget.value));
  };
  const handleChangeCompleteSlider = (value: number) => {
    setIsDragging(false);
    onChangeCompleteValues(value);
  };

  // Keep local display value in sync with external value when not dragging
  useEffect(() => {
    if (!isDragging) setDisplayValue(value);
  }, [value, isDragging]);

  return (
    <div className={clsx('flex items-center justify-between gap-3.5', className)}>
      <label
        htmlFor={inputId}
        className="text-button-sm flex items-center gap-1 font-medium whitespace-nowrap text-neutral-500"
      >
        <span>{label}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </label>
      <div className="flex w-full max-w-[128px] items-center gap-3">
        <Slider
          disabled={disabled}
          className="w-full max-w-[76px]"
          value={[displayValue ?? 0]}
          min={min}
          max={max}
          step={actualStep}
          onValueChange={([val]) => {
            // When slider changes, update value (input will update via props)
            setIsDragging(true);
            setDisplayValue(val);
            handleChange(val);
          }}
          onChangeComplete={([val]) => {
            handleChangeCompleteSlider(val);
          }}
        />
        <NumberField
          id={inputId}
          disabled={disabled}
          value={displayValue ?? 0} // input always gets latest on-drag value
          onChange={handleChange} // input changes update value (slider will update via props)
          min={min}
          max={max}
          step={actualStep}
          suffix={suffix}
          allowDecimal={allowDecimal}
          className={classNameInput}
          formatDisplayValue={formatDisplayValue}
          getNumericInput={getNumericInput}
          onChangeComplete={handleChangeComplete}
        />
      </div>
    </div>
  );
};
